import React, { useState, useEffect } from 'react';
import { Mail, Plus, X, CheckCircle, AlertTriangle, Send } from 'lucide-react';
import { api } from '../api';

const EMAIL_CATEGORIES = [
  {
    key: 'private_workshop_recipients',
    label: 'Private Workshop Requests',
    description: 'Notified when a partner submits a new private workshop request',
    icon: '📋',
    notifyKey: 'partner_notify_private_workshop',
  },
  {
    key: 'syndication_recipients',
    label: 'LMS Content Syndication Requests',
    description: 'Notified when a partner submits a content syndication (SCORM) request',
    icon: '🔗',
    notifyKey: 'partner_notify_syndication',
  },
  {
    key: 'new_content_recipients',
    label: 'New Content Requests',
    description: 'Notified when a partner submits a new content creation request (coming soon)',
    icon: '📝',
    notifyKey: 'partner_notify_new_content',
  },
  {
    key: 'hands_on_lab_recipients',
    label: 'Hands-on Lab Requests',
    description: 'Notified when a partner submits a hands-on lab access request (coming soon)',
    icon: '🔬',
    notifyKey: 'partner_notify_lab',
  },
  {
    key: 'access_capacity_recipients',
    label: 'Organization Capacity Alerts',
    description: 'Notified when an Access Control rule\'s Max Users limit reaches 90% usage',
    icon: '📊',
  },
  {
    key: 'weekly_report_recipients',
    label: 'Weekly Platform Usage Report',
    description: 'Receives a weekly summary of downloads, workshops, learners trained, new users and active partners (sent every Monday)',
    icon: '🗓️',
  },
];

const EmailSettings = () => {
  const [settings, setSettings] = useState({
    private_workshop_recipients: [],
    syndication_recipients: [],
    new_content_recipients: [],
    hands_on_lab_recipients: [],
    access_capacity_recipients: [],
    weekly_report_recipients: [],
    partner_notify_private_workshop: false,
    partner_notify_syndication: false,
    partner_notify_lab: false,
    partner_notify_new_content: false,
  });
  const [savedSettings, setSavedSettings] = useState(null);
  const [newEmail, setNewEmail] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveError, setSaveError] = useState('');
  const [testState, setTestState] = useState({});

  useEffect(() => {
    api.request('/admin/email-settings').then(data => {
      const parsed = {
        private_workshop_recipients: data.private_workshop_recipients || [],
        syndication_recipients: data.syndication_recipients || [],
        new_content_recipients: data.new_content_recipients || [],
        hands_on_lab_recipients: data.hands_on_lab_recipients || [],
        access_capacity_recipients: data.access_capacity_recipients || [],
        weekly_report_recipients: data.weekly_report_recipients || [],
        partner_notify_private_workshop: data.partner_notify_private_workshop || false,
        partner_notify_syndication: data.partner_notify_syndication || false,
        partner_notify_lab: data.partner_notify_lab || false,
        partner_notify_new_content: data.partner_notify_new_content || false,
      };
      setSettings(parsed);
      setSavedSettings(JSON.stringify(parsed));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const hasUnsavedChanges = savedSettings !== null && JSON.stringify(settings) !== savedSettings;

  const addEmail = (key) => {
    const email = (newEmail[key] || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (settings[key].includes(email)) return;
    setSettings(s => ({ ...s, [key]: [...s[key], email] }));
    setNewEmail(e => ({ ...e, [key]: '' }));
  };

  const removeEmail = (key, email) => {
    setSettings(s => ({ ...s, [key]: s[key].filter(e => e !== email) }));
  };

  const handleSave = async () => {
    setSaving(true); setSaveMsg(''); setSaveError('');
    try {
      await api.request('/admin/email-settings', { method: 'PUT', body: JSON.stringify(settings) });
      setSavedSettings(JSON.stringify(settings));
      setSaveMsg('Settings saved successfully.');
    } catch (e) {
      setSaveError(e.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
      setTimeout(() => { setSaveMsg(''); setSaveError(''); }, 4000);
    }
  };

  const handleTestEmail = async (cat) => {
    setTestState(s => ({ ...s, [cat.key]: 'sending' }));
    try {
      await api.request('/admin/email-settings/test', {
        method: 'POST',
        body: JSON.stringify({ category_key: cat.key, category_label: cat.label }),
      });
      setTestState(s => ({ ...s, [cat.key]: 'sent' }));
      setTimeout(() => setTestState(s => ({ ...s, [cat.key]: '' })), 5000);
    } catch (e) {
      setTestState(s => ({ ...s, [cat.key]: 'error:' + (e.message || 'Failed') }));
      setTimeout(() => setTestState(s => ({ ...s, [cat.key]: '' })), 6000);
    }
  };

  const [weeklyReportState, setWeeklyReportState] = useState('');
  const sendWeeklyReportNow = async () => {
    setWeeklyReportState('sending');
    try {
      await api.request('/admin/reports/weekly/send-now', { method: 'POST' });
      setWeeklyReportState('sent');
    } catch (e) {
      setWeeklyReportState('error:' + (e.message || 'Failed'));
    } finally {
      setTimeout(() => setWeeklyReportState(''), 6000);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin w-10 h-10 border-4 border-levelup-accent border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="email-settings-page">
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-levelup-accent/20 rounded-lg flex items-center justify-center">
              <Mail className="text-levelup-accent" size={22} />
            </div>
            <div>
              <h1 className="text-xl font-bold">Email Settings</h1>
              <p className="text-gray-300 mt-0.5 text-sm">Configure notification recipients for all request types</p>
            </div>
          </div>
          {hasUnsavedChanges && (
            <div className="flex items-center gap-2 bg-yellow-400/20 border border-yellow-400/40 px-4 py-2 rounded-lg text-yellow-300 text-sm font-medium">
              <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              Unsaved changes
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
        <h2 className="text-lg font-bold text-levelup-dark">Auto-Notify Partners on Approval / Rejection</h2>
        <p className="text-sm text-gray-500 mt-1">
          Each request type below has its own switch. When <strong>ON</strong> for a category, partners automatically receive an email
          the moment their request in that category is approved or rejected. Leave a category <strong>OFF</strong> to stay in testing mode
          for it (no partner emails sent) until you're ready to go live.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {EMAIL_CATEGORIES.map(cat => {
          const ts = testState[cat.key] || '';
          const isSending = ts === 'sending';
          const isSent = ts === 'sent';
          const isError = ts.startsWith('error:');
          const hasRecipients = settings[cat.key].length > 0;
          return (
            <div key={cat.key} className="bg-white rounded-xl shadow-md p-4 border border-gray-100 flex flex-col" data-testid={`email-category-${cat.key}`}>
              <div className="flex items-start justify-between gap-2 mb-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{cat.icon}</span>
                  <div>
                    <h3 className="font-bold text-levelup-dark">{cat.label}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{cat.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleTestEmail(cat)}
                  disabled={!hasRecipients || isSending}
                  data-testid={`test-email-btn-${cat.key}`}
                  title={hasRecipients ? `Send test email to ${settings[cat.key].length} recipient(s)` : 'Add recipients first'}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    isSent ? 'bg-green-100 text-green-700 border border-green-300'
                    : isError ? 'bg-red-100 text-red-600 border border-red-200'
                    : hasRecipients ? 'bg-levelup-accent/10 text-levelup-dark border border-levelup-accent/30 hover:bg-levelup-accent/20'
                    : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                  }`}
                >
                  {isSending ? (<><div className="animate-spin w-3 h-3 border-2 border-current border-t-transparent rounded-full" />Sending...</>)
                   : isSent ? (<><CheckCircle size={13} />Sent!</>)
                   : isError ? (<><AlertTriangle size={13} />{ts.replace('error:', '')}</>)
                   : (<><Send size={13} />Test Email</>)}
                </button>
              </div>
              <div className="space-y-2 mb-3 flex-1 min-h-[56px]">
                {settings[cat.key].length === 0 ? (
                  <p className="text-sm text-gray-400 italic py-2">No recipients — notifications will not be sent.</p>
                ) : (
                  settings[cat.key].map(email => (
                    <div key={email} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-700 font-mono truncate">{email}</span>
                      <button onClick={() => removeEmail(cat.key, email)} className="text-gray-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0" data-testid={`remove-email-${cat.key}`} title="Remove">
                        <X size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2 mt-auto">
                <input
                  type="email"
                  value={newEmail[cat.key] || ''}
                  onChange={e => setNewEmail(n => ({ ...n, [cat.key]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && addEmail(cat.key)}
                  placeholder="Add recipient email..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
                  data-testid={`add-email-input-${cat.key}`}
                />
                <button onClick={() => addEmail(cat.key)} className="px-3 py-2 bg-levelup-dark text-white rounded-lg text-sm font-medium hover:bg-levelup-teal transition-colors flex items-center gap-1 flex-shrink-0" data-testid={`add-email-btn-${cat.key}`}>
                  <Plus size={16} /> Add
                </button>
              </div>
              {isSent && <p className="text-xs text-green-600 mt-2 flex items-center gap-1"><CheckCircle size={12} /> Test email sent to {settings[cat.key].join(', ')}</p>}
              {isError && <p className="text-xs text-red-500 mt-2">{ts.replace('error:', '')}</p>}
              {cat.key === 'weekly_report_recipients' && (
                <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Sent automatically every Monday to the recipients above.</p>
                  <button
                    onClick={sendWeeklyReportNow}
                    disabled={!hasRecipients || weeklyReportState === 'sending'}
                    data-testid="send-weekly-report-now-btn"
                    title={hasRecipients ? 'Send this week\'s report immediately' : 'Add recipients first'}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      weeklyReportState === 'sent' ? 'bg-green-100 text-green-700 border border-green-300'
                      : weeklyReportState.startsWith('error:') ? 'bg-red-100 text-red-600 border border-red-200'
                      : hasRecipients ? 'bg-levelup-dark/10 text-levelup-dark border border-levelup-dark/30 hover:bg-levelup-dark/20'
                      : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                    }`}
                  >
                    {weeklyReportState === 'sending' ? (<><div className="animate-spin w-3 h-3 border-2 border-current border-t-transparent rounded-full" />Sending...</>)
                     : weeklyReportState === 'sent' ? (<><CheckCircle size={13} />Sent!</>)
                     : weeklyReportState.startsWith('error:') ? (<><AlertTriangle size={13} />{weeklyReportState.replace('error:', '')}</>)
                     : (<><Send size={13} />Send Report Now</>)}
                  </button>
                </div>
              )}
              {cat.notifyKey && (
                <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-100">
                  <div>
                    <p className="text-xs font-semibold text-gray-700">Notify partner on approval/rejection</p>
                    <p className={`text-xs mt-0.5 font-medium ${settings[cat.notifyKey] ? 'text-green-600' : 'text-gray-400'}`}>
                      {settings[cat.notifyKey] ? 'ON — partner will be emailed' : 'OFF (testing mode)'}
                    </p>
                  </div>
                  <button
                    onClick={() => setSettings(s => ({ ...s, [cat.notifyKey]: !s[cat.notifyKey] }))}
                    data-testid={`partner-notify-toggle-${cat.key}`}
                    className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${settings[cat.notifyKey] ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${settings[cat.notifyKey] ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={`flex items-center gap-4 pb-4 ${hasUnsavedChanges ? 'sticky bottom-4 z-10' : ''}`}>
        <div className={`flex items-center gap-4 px-4 py-2.5 rounded-xl transition-all w-full ${hasUnsavedChanges ? 'bg-white shadow-lg border border-levelup-accent/30' : ''}`}>
          <button onClick={handleSave} disabled={saving} className="px-8 py-3 bg-levelup-dark text-white rounded-xl font-semibold hover:bg-levelup-teal transition-colors disabled:opacity-60 flex items-center gap-2" data-testid="save-email-settings-btn">
            {saving ? <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />Saving...</> : 'Save Settings'}
          </button>
          {hasUnsavedChanges && !saving && !saveMsg && <span className="text-sm text-amber-600 font-medium">You have unsaved changes</span>}
          {saveMsg && <span className="text-green-600 font-medium flex items-center gap-1"><CheckCircle size={16} />{saveMsg}</span>}
          {saveError && <span className="text-red-500 text-sm">{saveError}</span>}
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
